import React from 'react';
import { Pencil } from 'lucide-react';
import { BaseComponentImpl } from '../../../../../types/implementations';
import { Building } from '../../../../../types/protocols';
import { getBuildComponentTypeTranslation } from '../../../../../Helpers/BuildComponentTypesStore';

interface ComponentHeaderProps {
    component: BaseComponentImpl;
    building: Building | null;
    buildingKey: string;
    hasLifetime: boolean;
    displayArea: string;
    /** Today's condition (0–10), degraded since the assessment; null when not assessed */
    currentCondition: number | null;
    /** Years left before replacement, as in "Ersätt komponent"; null without condition or lifetime */
    yearsRemaining: number | null;
    onOpenLifetime: () => void;
    onOpenAreaModal: () => void;
    onOpenConditionModal: () => void;
    disabled?: boolean;
}

/**
 * Component header displaying name, type, and metadata
 */
export const ComponentHeader: React.FC<ComponentHeaderProps> = ({
    component,
    building,
    buildingKey,
    hasLifetime,
    displayArea,
    currentCondition,
    yearsRemaining,
    onOpenLifetime,
    onOpenAreaModal,
    onOpenConditionModal,
    disabled = false
}) => {
    return (
        <div className="component-header-row">
            {/* Component Title */}
            <div className="component-title">
                <h6 className="component-name">
                    <a
                        href={`/building/${buildingKey}/component/${component.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="component-link"
                    >
                        {building && component.displayName(building)}
                    </a>
                </h6>
                <small className="component-type text-muted">
                    {component.specificType?.value && getBuildComponentTypeTranslation(component.specificType.value, component.type)}
                </small>
            </div>

            {/* Component Metadata */}
            <div className="component-metadata-section">
                {/* Area/Mängd */}
                <div className="metadata-item">
                    <span className="metadata-label">Mängd</span>
                    <div className="metadata-value">
                        <span className={disabled ? 'text-muted' : ''}>
                            {displayArea}
                        </span>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled) {
                                    onOpenAreaModal();
                                }
                            }}
                            disabled={disabled}
                            title="Redigera mängd"
                            style={{ lineHeight: 1 }}
                        >
                            <Pencil size={12} className={disabled ? 'text-muted' : 'text-primary'} />
                        </button>
                    </div>
                </div>

                {/* Condition/Skick: the same value and modal as the replacement row */}
                <div className="metadata-item">
                    <span className="metadata-label">Skick</span>
                    <div className="metadata-value">
                        <span className={disabled || currentCondition === null ? 'text-muted' : ''}>
                            {currentCondition === null ? 'Ej bedömt' : `${currentCondition.toFixed(1)}/10`}
                            {yearsRemaining !== null && ` · ${Math.max(0, Math.floor(yearsRemaining))} år kvar`}
                        </span>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled) {
                                    onOpenConditionModal();
                                }
                            }}
                            disabled={disabled}
                            title="Ändra skick"
                            style={{ lineHeight: 1 }}
                        >
                            <Pencil size={12} className={disabled ? 'text-muted' : 'text-primary'} />
                        </button>
                    </div>
                </div>

                {/* Missing lifetime warning */}
                {!hasLifetime && (
                    <div className="metadata-item metadata-warning">
                        <span className="metadata-label text-danger">Livslängd</span>
                        <div className="metadata-value">
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!disabled) {
                                        onOpenLifetime();
                                    }
                                }}
                                disabled={disabled}
                            >
                                Ange
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Condition comment if exists */}
            {component.condition?.value?.comment && (
                <div className="component-comment">
                    <small className="text-muted">{component.condition.value.comment}</small>
                </div>
            )}
        </div>
    );
};
